import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import ProtectedRoute from "@/components/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { AppLayout } from "@/components/layout/AppLayout";

// ---------------------------------------------------------------------------
// Lazy-loaded pages
// ---------------------------------------------------------------------------

const Login = lazy(() => import("@/pages/Login"));
const HomePage = lazy(() => import("@/pages/HomePage"));
const RoleSelectPage = lazy(() => import("@/pages/RoleSelectPage"));

// Dashboard
const UnifiedDashboard = lazy(() => import("@/pages/Dashboard/UnifiedDashboard"));

// ESS — Profile, Help Desk, Directory
const MyProfilePage = lazy(() => import("@/pages/Profile/MyProfilePage"));
const AdminDocumentsPage = lazy(() => import("@/pages/Documents/AdminDocumentsPage"));
const HelpDeskPage = lazy(() => import("@/pages/HelpDesk/HelpDeskPage"));
const DirectoryPage = lazy(() => import("@/pages/Directory/DirectoryPage"));

// Employees
const EmployeeListPage = lazy(() => import("@/pages/Employees/EmployeeListPage"));
const EmployeeDetailPage = lazy(() => import("@/pages/Employees/EmployeeDetailPage"));
const EmployeeFormCreate = lazy(() =>
  import("@/pages/Employees/EmployeeForm").then((m) => ({ default: () => m.default({ mode: "create" }) }))
);
const EmployeeFormEdit = lazy(() =>
  import("@/pages/Employees/EmployeeForm").then((m) => ({ default: () => m.default({ mode: "edit" }) }))
);

// Org Chart
const OrgChartPage = lazy(() => import("@/pages/OrgChart/OrgChartPage"));

// Policies
const PolicyListPage = lazy(() => import("@/pages/Policies/PolicyListPage"));
const PolicyDetailPage = lazy(() => import("@/pages/Policies/PolicyDetailPage"));
const MyPoliciesPage = lazy(() => import("@/pages/Policies/MyPoliciesPage"));
const PolicyViewPage = lazy(() => import("@/pages/Policies/PolicyViewPage"));

// Notes
const NotesPage = lazy(() => import("@/pages/Notes/NotesPage"));

// Reports
const ReportsHubPage = lazy(() => import("@/pages/Reports/ReportsHubPage"));
const ReportRunnerPage = lazy(() => import("@/pages/Reports/ReportRunnerPage"));
const ReportsDashboardPage = lazy(() => import("@/pages/Reports/ReportsDashboardPage"));
const SavedReportsPage = lazy(() => import("@/pages/Reports/SavedReportsPage"));

// Loan requests
const MyLoanRequestsPage = lazy(() => import("@/pages/Loans/MyLoanRequestsPage"));
const LoanRequestsAdminPage = lazy(() => import("@/pages/Loans/LoanRequestsAdminPage"));
const LoanRequestDetailPage = lazy(() => import("@/pages/Loans/LoanRequestDetailPage"));
const LoanApproverConfigPage = lazy(() => import("@/pages/Loans/LoanApproverConfigPage"));

// Letters
const LetterDashboardPage = lazy(() => import("@/pages/Letters/LetterDashboardPage"));
const LetterListPage = lazy(() => import("@/pages/Letters/LetterListPage"));
const MyLettersPage = lazy(() => import("@/pages/Letters/MyLettersPage"));
const LetterTemplateListPage = lazy(() => import("@/pages/Letters/LetterTemplateListPage"));
const LetterTemplateEditorPage = lazy(() => import("@/pages/Letters/LetterTemplateEditorPage"));
const GenerateLetterPage = lazy(() => import("@/pages/Letters/GenerateLetterPage"));

// Probation
const ProbationDashboardPage = lazy(() => import("@/pages/Probation/ProbationDashboardPage"));
const ProbationDetailPage = lazy(() => import("@/pages/Probation/ProbationDetailPage"));

// Assets
const AssetListPage = lazy(() => import("@/pages/Assets/AssetListPage"));
const AddAssetPage = lazy(() => import("@/pages/Assets/AddAssetPage"));
const AssetDetailPage = lazy(() => import("@/pages/Assets/AssetDetailPage"));
const AssetMyPage = lazy(() => import("@/pages/Assets/MyAssetsPage"));
const RequestAssetPage = lazy(() => import("@/pages/Assets/RequestAssetPage"));
const AssetRequestsPage = lazy(() => import("@/pages/Assets/AssetRequestsPage"));
const AssetReportsPage = lazy(() => import("@/pages/Assets/AssetReportsPage"));
const AssetClearanceInboxPage = lazy(() =>
  import("@/pages/Assets/AssetClearanceInboxPage"),
);
const RequestSaaSAccessPage = lazy(() =>
  import("@/pages/SaaS/RequestSaaSAccessPage"),
);
const ManagerSaaSApprovalsPage = lazy(() =>
  import("@/pages/SaaS/ManagerSaaSApprovalsPage"),
);
const SaaSAccessHistoryPage = lazy(() =>
  import("@/pages/SaaS/SaaSAccessHistoryPage"),
);

// Benefits
const BenefitsSummaryPage = lazy(() => import("@/pages/Benefits/BenefitsSummaryPage"));

// Recruitment
const RecruitmentDashboardPage = lazy(
  () => import("@/pages/Recruitment/RecruitmentDashboardPage")
);
const JobRequisitionsPage = lazy(() => import("@/pages/Recruitment/JobRequisitionsPage"));
const CandidatePipelinePage = lazy(() => import("@/pages/Recruitment/CandidatePipelinePage"));
const IntakeCockpitPage = lazy(() => import("@/pages/Recruitment/IntakeCockpitPage"));

// Pre-Onboarding
const PreOnboardingPortal = lazy(() => import("@/pages/PreOnboarding/PreOnboardingPortal"));
const PublicAnonymousSurveyPage = lazy(
  () => import("@/pages/Engagement/PublicAnonymousSurveyPage"),
);
const PreOnboardingAdminPage = lazy(() => import("@/pages/PreOnboarding/PreOnboardingAdminPage"));

// IT Clearance form (public token portal)
const ITClearanceFormPortal = lazy(() =>
  import("@/pages/ITClearanceForm/ITClearanceFormPortal"),
);
// HR/IT preview of the form layout (admin-side, no token needed)
const ITClearanceFormPreviewPage = lazy(() =>
  import("@/pages/ITClearanceForm/ITClearanceFormPreviewPage"),
);

// Visa & Immigration
const VisaDashboardPage = lazy(() => import("@/pages/Visa/VisaDashboardPage"));

// Compensation (#122)
const CompensationRevisionsPage = lazy(() => import("@/pages/Compensation/CompensationRevisionsPage"));

// Compliance Training (#124)
const ComplianceTrainingPage = lazy(() => import("@/pages/Compliance/ComplianceTrainingPage"));

// Leave
const LeaveDashboardPage = lazy(() => import("@/pages/Leave/LeaveDashboardPage"));
const ApplyLeavePage = lazy(() => import("@/pages/Leave/ApplyLeavePage"));
const MyLeavePage = lazy(() => import("@/pages/Leave/MyLeavePage"));
const TeamCalendarPage = lazy(() => import("@/pages/Leave/TeamCalendarPage"));
const LeaveApprovalsPage = lazy(() => import("@/pages/Leave/LeaveApprovalsPage"));
const LeaveAdminPage = lazy(() => import("@/pages/Leave/LeaveAdminPage"));
const LeaveManagementPage = lazy(() => import("@/pages/Leave/LeaveManagementPage"));
const LeaveAdminPendingPage = lazy(() =>
  import("@/pages/Leave/LeaveAdminPendingPage"),
);
const YearEndProcessingPage = lazy(() => import("@/pages/Leave/YearEndProcessingPage"));
const LeaveReportsPage = lazy(() => import("@/pages/Leave/LeaveReportsPage"));
const ApplyWfhPage = lazy(() => import("@/pages/Leave/ApplyWfhPage"));

// Attendance
const AttendancePage = lazy(() => import("@/pages/Attendance/AttendancePage"));
const MyTimesheetsPage = lazy(() => import("@/pages/Attendance/MyTimesheetsPage"));
const TeamAttendancePage = lazy(() => import("@/pages/Attendance/TeamAttendancePage"));

// Onboarding
const MyOnboardingPage = lazy(() => import("@/pages/Onboarding/MyOnboardingPage"));
const AdminOnboardingPage = lazy(() => import("@/pages/Onboarding/AdminOnboardingPage"));

// Payroll
const PayrollDashboardPage = lazy(() => import("@/pages/Payroll/PayrollDashboardPage"));
const PayrollRunPage = lazy(() => import("@/pages/Payroll/PayrollRunPage"));
const NewPayrollRunPage = lazy(() => import("@/pages/Payroll/NewPayrollRunPage"));
const BankFileBuilderPage = lazy(
  () => import("@/pages/Payroll/BankFileBuilderPage"),
);
const MyPayslipsPage = lazy(() => import("@/pages/Payroll/MyPayslipsPage"));
const HistoricalPayslipsUploadPage = lazy(
  () => import("@/pages/Payroll/HistoricalPayslipsUploadPage"),
);
const MyTaxDeclarationPage = lazy(() => import("@/pages/Payroll/MyTaxDeclarationPage"));
const TaxDeclarationsAdminPage = lazy(
  () => import("@/pages/Payroll/TaxDeclarationsAdminPage"),
);
const StatutoryReportsPage = lazy(() => import("@/pages/Payroll/StatutoryReportsPage"));
const SalaryComponentsPage = lazy(() => import("@/pages/Payroll/SalaryComponentsPage"));
const LoansPage = lazy(() => import("@/pages/Payroll/LoansPage"));
const LoansReportPage = lazy(() => import("@/pages/Payroll/LoansReportPage"));
const VariablePayPage = lazy(() => import("@/pages/Payroll/VariablePayPage"));
const FnFCertificatesPage = lazy(() => import("@/pages/Payroll/FnFCertificatesPage"));
const PerquisitesPage = lazy(() => import("@/pages/Payroll/PerquisitesPage"));
const SalaryRevisionsPage = lazy(() => import("@/pages/Payroll/SalaryRevisionsPage"));
const SalaryStructurePage = lazy(() => import("@/pages/Payroll/SalaryStructurePage"));

// Performance
const PerformanceDashboardPage = lazy(
  () => import("@/pages/Performance/PerformanceDashboardPage")
);
const GoalsPage = lazy(() => import("@/pages/Performance/GoalsPage"));
const PIPTrackerPage = lazy(() => import("@/pages/Performance/PIPTrackerPage"));
const MyPIPPage = lazy(() => import("@/pages/Performance/MyPIPPage"));

// Disciplinary & Grievance
const DisciplinaryCasesPage = lazy(
  () => import("@/pages/Disciplinary/DisciplinaryCasesPage")
);
const WhistleblowerPortalPage = lazy(
  () => import("@/pages/Disciplinary/WhistleblowerPortalPage")
);
const WhistleblowerAdminPage = lazy(
  () => import("@/pages/Disciplinary/WhistleblowerAdminPage")
);

// Redundancy Management
const RedundancyPlanPage = lazy(
  () => import("@/pages/Redundancy/RedundancyPlanPage")
);

// Expenses
const ExpenseListPage = lazy(() => import("@/pages/Expenses/ExpenseListPage"));
const ExpenseDetailPage = lazy(() => import("@/pages/Expenses/ExpenseDetailPage"));
const ExpenseCategoriesAdminPage = lazy(
  () => import("@/pages/Expenses/ExpenseCategoriesAdminPage")
);
const ExpensePendingApprovalsPage = lazy(
  () => import("@/pages/Expenses/ExpensePendingApprovalsPage")
);

// Engagement
const RecognitionFeedPage = lazy(
  () => import("@/pages/Engagement/RecognitionFeedPage")
);

// Notifications — full activity list reached from the bell popover footer.
const NotificationsPage = lazy(() => import("@/pages/NotificationsPage"));

// Separation & FnF
const SeparationDashboardPage = lazy(
  () => import("@/pages/Separation/SeparationDashboardPage")
);
const NoticePeriodPage = lazy(
  () => import("@/pages/Separation/NoticePeriodPage")
);
const SeparationDetailPage = lazy(
  () => import("@/pages/Separation/SeparationDetailPage")
);
const InitiateExitPage = lazy(
  () => import("@/pages/Separation/InitiateExitPage")
);
const SelfResignationPage = lazy(
  () => import("@/pages/Separation/SelfResignationPage")
);
const ResignationApprovalsPage = lazy(
  () => import("@/pages/Separation/ResignationApprovalsPage")
);

// Analytics
const AnalyticsDashboardPage = lazy(() => import("@/pages/Analytics/AnalyticsDashboardPage"));
const ReportBuilderPage = lazy(() => import("@/pages/Analytics/ReportBuilderPage"));
const HeadcountDetailPage = lazy(() => import("@/pages/Analytics/HeadcountDetailPage"));
const AttritionDetailPage = lazy(() => import("@/pages/Analytics/AttritionDetailPage"));
const LeaveDetailPage = lazy(() => import("@/pages/Analytics/LeaveDetailPage"));
const PerformanceDetailPage = lazy(() => import("@/pages/Analytics/PerformanceDetailPage"));
const ComplianceDetailPage = lazy(() => import("@/pages/Analytics/ComplianceDetailPage"));

// Learning & Development
const LearningDashboardPage = lazy(
  () => import("@/pages/Learning/LearningDashboardPage")
);
const SkillDevelopmentCompliancePage = lazy(
  () => import("@/pages/Learning/SkillDevelopmentCompliancePage")
);
const CoursePlayerPage = lazy(
  () => import("@/pages/Learning/CoursePlayerPage")
);

// Accommodation & Disability
const AccommodationRequestPage = lazy(
  () => import("@/pages/Accommodation/AccommodationRequestPage")
);

// Onboarding Feedback
const OnboardingFeedbackFormPage = lazy(
  () => import("@/pages/Engagement/OnboardingFeedbackFormPage")
);
const OnboardingFeedbackAdminPage = lazy(
  () => import("@/pages/Engagement/OnboardingFeedbackAdminPage")
);
const OnboardingFeedbackTemplatesPage = lazy(
  () => import("@/pages/Engagement/OnboardingFeedbackTemplatesPage")
);
const EngagementSurveysAdminPage = lazy(
  () => import("@/pages/Engagement/EngagementSurveysAdminPage")
);
const MySurveysPage = lazy(() => import("@/pages/Engagement/MySurveysPage"));
const MySurveySubmitPage = lazy(
  () => import("@/pages/Engagement/MySurveySubmitPage")
);

// Access Audit — self-serve email reconciliation tool (any authenticated user)
const AccessAuditPage = lazy(() => import("@/pages/AccessAudit/AccessAuditPage"));

// Admin
const ManageUsersPage = lazy(() => import("@/pages/Admin/ManageUsersPage"));
const HRNotificationSettingsPage = lazy(() => import("@/pages/Admin/HRNotificationSettingsPage"));
const HolidayAdminPage = lazy(() => import("@/pages/Admin/HolidayAdminPage"));
const OrgStructurePage = lazy(() => import("@/pages/Admin/OrgStructurePage"));
const LegalEntitiesAdminPage = lazy(() => import("@/pages/Admin/LegalEntitiesAdminPage"));
const LegalEntityReportsPage = lazy(() => import("@/pages/Admin/LegalEntityReportsPage"));
const SlackChannelsPage = lazy(() => import("@/pages/Admin/SlackChannelsPage"));
const DeprovisionAuditPage = lazy(() => import("@/pages/Admin/DeprovisionAuditPage"));
const SystemAuditLogPage = lazy(() => import("@/pages/Admin/SystemAuditLogPage"));
const PerksAdminPage = lazy(() => import("@/pages/Admin/PerksAdminPage"));

// Manager Self-Service Portal
const ManagerDashboardPage = lazy(() => import("@/pages/Manager/ManagerDashboardPage"));
const ApprovalHubPage = lazy(() => import("@/pages/Manager/ApprovalHubPage"));
const TeamAnalyticsPage = lazy(() => import("@/pages/Manager/TeamAnalyticsPage"));

// ---------------------------------------------------------------------------
// Loader
// ---------------------------------------------------------------------------

function PageLoader() {
  return (
    <div className="flex h-screen w-full items-center justify-center bg-zinc-50">
      <div className="flex flex-col items-center gap-4">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-zinc-200 border-t-zinc-700" />
        <p className="text-sm text-zinc-500">Loading...</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Layout wrapper helper
// ---------------------------------------------------------------------------

function WithLayout({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute>
      <AppLayout>{children}</AppLayout>
    </ProtectedRoute>
  );
}

// ---------------------------------------------------------------------------
// App
// ---------------------------------------------------------------------------

export default function App() {
  return (
    <ErrorBoundary surface="app-root">
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<Login />} />

        {/* Role selection — authenticated but pre-role-picked. Not wrapped in
            AppLayout because the sidebar would key off active_role which is
            null at this point. The page itself enforces that user is loaded. */}
        <Route path="/select-role" element={<RoleSelectPage />} />

        {/* Protected — with AppLayout */}
        <Route
          path="/dashboard"
          element={
            <WithLayout>
              <UnifiedDashboard />
            </WithLayout>
          }
        />

        {/* ESS — Profile, Help Desk, Directory */}
        <Route
          path="/profile"
          element={
            <WithLayout>
              <MyProfilePage />
            </WithLayout>
          }
        />
        <Route
          path="/notifications"
          element={
            <WithLayout>
              <NotificationsPage />
            </WithLayout>
          }
        />

        {/* Access Audit — open to any authenticated user (no role gate).
            The endpoint returns only status + name + last_day for emails
            the caller submitted, not a full directory dump. */}
        <Route
          path="/access-audit"
          element={
            <WithLayout>
              <AccessAuditPage />
            </WithLayout>
          }
        />
        <Route
          path="/help"
          element={
            <WithLayout>
              <HelpDeskPage />
            </WithLayout>
          }
        />
        <Route
          path="/directory"
          element={
            <WithLayout>
              <DirectoryPage />
            </WithLayout>
          }
        />

        {/* Employees — permission-gated (any of employee.list / employee.view) */}
        <Route
          path="/employees"
          element={
            <ProtectedRoute permissions={["employee.list", "employee.view"]}>
              <AppLayout>
                <EmployeeListPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employees/new"
          element={
            <ProtectedRoute permissions={["employee.create"]}>
              <AppLayout>
                <EmployeeFormCreate />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employees/:id"
          element={
            <ProtectedRoute permissions={["employee.list", "employee.view", "employee.view_all", "employee.view_team"]}>
              <AppLayout>
                <EmployeeDetailPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employees/:id/edit"
          element={
            <ProtectedRoute permissions={["employee.edit"]}>
              <AppLayout>
                <EmployeeFormEdit />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Org Chart */}
        <Route
          path="/org-chart"
          element={
            <WithLayout>
              <OrgChartPage />
            </WithLayout>
          }
        />

        {/* Policies — /my and /view/:id must come before /:id */}
        <Route
          path="/policies/my"
          element={
            <WithLayout>
              <MyPoliciesPage />
            </WithLayout>
          }
        />
        <Route
          path="/policies/view/:id"
          element={
            <WithLayout>
              <PolicyViewPage />
            </WithLayout>
          }
        />
        <Route
          path="/policies"
          element={
            <WithLayout>
              <PolicyListPage />
            </WithLayout>
          }
        />
        <Route
          path="/policies/:id"
          element={
            <WithLayout>
              <PolicyDetailPage />
            </WithLayout>
          }
        />

        {/* Reports — centralised hub + dashboard + generic runner.
            Static paths come BEFORE the :reportId catch-all so React
            Router doesn't match "dashboard" as a report id. The runner
            picks any registered report by id, so new reports don't
            require new routes. */}
        <Route
          path="/reports"
          element={
            <WithLayout>
              <ReportsHubPage />
            </WithLayout>
          }
        />
        <Route
          path="/reports/dashboard"
          element={
            <WithLayout>
              <ReportsDashboardPage />
            </WithLayout>
          }
        />
        <Route
          path="/reports/saved"
          element={
            <WithLayout>
              <SavedReportsPage />
            </WithLayout>
          }
        />

        {/* Loan requests — employees raise on /loans/my, HR approves on
            /loans/requests. Permission gates on the backend; nav entry
            is gated in AppLayout. */}
        <Route
          path="/loans/my"
          element={
            <WithLayout>
              <MyLoanRequestsPage />
            </WithLayout>
          }
        />
        <Route
          path="/loans/requests"
          element={
            <WithLayout>
              <ErrorBoundary surface="loan-requests-list">
                <LoanRequestsAdminPage />
              </ErrorBoundary>
            </WithLayout>
          }
        />
        {/* Per-request detail page — accessible to the owner, HR, and
            the named manager. Backend's GET endpoint enforces the
            triple-or gate; permission gate here is intentionally loose
            so the email/Slack deep-link works for the manager too.
            Per-route ErrorBoundary catches any render-time throw and
            shows a visible recoverable UI instead of the blank screen
            that was reported repeatedly on this URL. */}
        <Route
          path="/loans/requests/:id"
          element={
            <WithLayout>
              <ErrorBoundary surface="loan-request-detail">
                <LoanRequestDetailPage />
              </ErrorBoundary>
            </WithLayout>
          }
        />
        <Route
          path="/loans/approver-config"
          element={
            <WithLayout>
              <LoanApproverConfigPage />
            </WithLayout>
          }
        />
        <Route
          path="/reports/:reportId"
          element={
            <WithLayout>
              <ReportRunnerPage />
            </WithLayout>
          }
        />

        {/* Letters — static-path routes MUST come before /:letterId so
            FastAPI / React Router doesn't match "my" / "dashboard" /
            "templates" / "generate" as a UUID. */}
        <Route
          path="/letters/my"
          element={
            <WithLayout>
              <MyLettersPage />
            </WithLayout>
          }
        />
        <Route
          path="/letters/dashboard"
          element={
            <WithLayout>
              <LetterDashboardPage />
            </WithLayout>
          }
        />
        <Route
          path="/letters/generate"
          element={
            <WithLayout>
              <GenerateLetterPage />
            </WithLayout>
          }
        />
        <Route
          path="/letters/templates"
          element={
            <WithLayout>
              <LetterTemplateListPage />
            </WithLayout>
          }
        />
        <Route
          path="/letters/templates/new"
          element={
            <WithLayout>
              <LetterTemplateEditorPage />
            </WithLayout>
          }
        />
        <Route
          path="/letters/templates/:id/edit"
          element={
            <WithLayout>
              <LetterTemplateEditorPage />
            </WithLayout>
          }
        />
        <Route
          path="/letters"
          element={
            <WithLayout>
              <LetterListPage />
            </WithLayout>
          }
        />

        {/* Notes / shared todos */}
        <Route
          path="/notes"
          element={
            <WithLayout>
              <NotesPage />
            </WithLayout>
          }
        />

        {/* Probation */}
        <Route
          path="/probation"
          element={
            <WithLayout>
              <ProbationDashboardPage />
            </WithLayout>
          }
        />
        <Route
          path="/probation/:id"
          element={
            <WithLayout>
              <ProbationDetailPage />
            </WithLayout>
          }
        />

        {/* Assets — static paths must come before /:id */}
        {/* /assets/requests and /assets (inventory) blocked for employee role */}
        <Route
          path="/assets/requests"
          element={
            <ProtectedRoute permissions={["assets.view", "assets.manage"]}>
              <AppLayout>
                <AssetRequestsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/assets/reports"
          element={
            <ProtectedRoute permissions={["assets.view", "assets.manage"]}>
              <AppLayout>
                <AssetReportsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/assets/clearance"
          element={
            <ProtectedRoute permissions={["assets.view", "assets.manage"]}>
              <AppLayout>
                <AssetClearanceInboxPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* /assets/request (submit request) open to all authenticated users */}
        <Route
          path="/assets/request"
          element={
            <WithLayout>
              <RequestAssetPage />
            </WithLayout>
          }
        />
        {/* SaaS access — /saas/request (submit) + /saas/my (own list)
            both point to the same page. Open to any authenticated user. */}
        <Route
          path="/saas/request"
          element={
            <WithLayout>
              <RequestSaaSAccessPage />
            </WithLayout>
          }
        />
        <Route
          path="/saas/my"
          element={
            <WithLayout>
              <RequestSaaSAccessPage />
            </WithLayout>
          }
        />
        {/* Manager view of team's SaaS access requests. The reporting
            manager approves + grants in one step; the previous HR-side
            /saas/requests provisioning queue was removed 2026-07-08. */}
        <Route
          path="/manager/saas-approvals"
          element={
            <WithLayout>
              <ManagerSaaSApprovalsPage />
            </WithLayout>
          }
        />
        {/* HR / superadmin — org-wide, read-only history + audit trail
            for every SaaS access request. Gated on saas.view_all so
            regular employees and managers hit ProtectedRoute and get
            403-equivalent redirect. */}
        <Route
          path="/saas/history"
          element={
            <ProtectedRoute permissions={["saas.view_all"]}>
              <AppLayout>
                <SaaSAccessHistoryPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/assets/new"
          element={
            <ProtectedRoute permissions={["assets.manage"]}>
              <AppLayout>
                <AddAssetPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* /assets/my open to all authenticated users */}
        <Route
          path="/assets/my"
          element={
            <WithLayout>
              <AssetMyPage />
            </WithLayout>
          }
        />
        <Route
          path="/assets"
          element={
            <ProtectedRoute permissions={["assets.view", "assets.manage"]}>
              <AppLayout>
                <AssetListPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/assets/:id"
          element={
            <ProtectedRoute permissions={["assets.view", "assets.manage"]}>
              <AppLayout>
                <AssetDetailPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Leave */}
        <Route
          path="/leave"
          element={
            <WithLayout>
              <LeaveDashboardPage />
            </WithLayout>
          }
        />
        <Route
          path="/leave/apply"
          element={
            <WithLayout>
              <ApplyLeavePage />
            </WithLayout>
          }
        />
        <Route
          path="/leave/wfh"
          element={
            <WithLayout>
              <ApplyWfhPage />
            </WithLayout>
          }
        />
        <Route
          path="/leave/my"
          element={
            <WithLayout>
              <MyLeavePage />
            </WithLayout>
          }
        />
        <Route
          path="/leave/team-calendar"
          element={
            <ProtectedRoute
              permissions={["leave.approve"]}
              roles={["manager", "hr_admin", "hr_country", "superadmin"]}
            >
              <AppLayout>
                <TeamCalendarPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/leave/approvals"
          element={
            <ProtectedRoute
              permissions={["leave.approve"]}
              roles={["manager", "hr_admin", "hr_country", "superadmin"]}
            >
              <AppLayout>
                <LeaveApprovalsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/leave/admin"
          element={
            <ProtectedRoute permissions={["leave.admin"]}>
              <AppLayout>
                <LeaveAdminPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/leave/management"
          element={
            <ProtectedRoute permissions={["leave.admin"]}>
              <AppLayout>
                <LeaveManagementPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/leave/admin/year-end"
          element={
            <ProtectedRoute permissions={["leave.admin"]}>
              <AppLayout>
                <YearEndProcessingPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/leave/admin/reports"
          element={
            <ProtectedRoute permissions={["leave.admin"]}>
              <AppLayout>
                <LeaveReportsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/leave/admin/pending"
          element={
            <ProtectedRoute permissions={["leave.admin"]}>
              <AppLayout>
                <LeaveAdminPendingPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Attendance */}
        <Route
          path="/attendance"
          element={
            <WithLayout>
              <AttendancePage />
            </WithLayout>
          }
        />
        <Route
          path="/attendance/timesheets"
          element={
            <WithLayout>
              <MyTimesheetsPage />
            </WithLayout>
          }
        />
        <Route
          path="/attendance/team"
          element={
            <ProtectedRoute
              permissions={["leave.approve"]}
              roles={["manager", "hr_admin", "hr_country", "superadmin"]}
            >
              <AppLayout>
                <TeamAttendancePage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Onboarding */}
        <Route
          path="/onboarding/me"
          element={
            <WithLayout>
              <MyOnboardingPage />
            </WithLayout>
          }
        />
        <Route
          path="/onboarding/admin"
          element={
            <ProtectedRoute permissions={["employee.edit"]}>
              <AppLayout>
                <AdminOnboardingPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Recruitment — permission-gated (any of recruitment.view / recruitment.manage) */}
        <Route
          path="/recruitment"
          element={
            <ProtectedRoute permissions={["recruitment.view", "recruitment.manage"]}>
              <AppLayout>
                <RecruitmentDashboardPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/recruitment/requisitions"
          element={
            <ProtectedRoute permissions={["recruitment.view", "recruitment.manage"]}>
              <AppLayout>
                <JobRequisitionsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/recruitment/pipeline"
          element={
            <ProtectedRoute permissions={["recruitment.view", "recruitment.manage"]}>
              <AppLayout>
                <CandidatePipelinePage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/recruitment/intake"
          element={
            <ProtectedRoute permission="recruitment.manage">
              <AppLayout>
                <IntakeCockpitPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Benefits */}
        <Route
          path="/benefits"
          element={
            <WithLayout>
              <BenefitsSummaryPage />
            </WithLayout>
          }
        />

        {/* Payroll — permission-gated (any of payroll.view / payroll.run / payroll.approve) */}
        <Route
          path="/payroll"
          element={
            <ProtectedRoute permissions={["payroll.view", "payroll.run", "payroll.approve"]}>
              <AppLayout>
                <PayrollDashboardPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll/new"
          element={
            <ProtectedRoute permissions={["payroll.run", "payroll.approve", "payroll.admin"]}>
              <AppLayout>
                <NewPayrollRunPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll/runs/:id"
          element={
            <ProtectedRoute permissions={["payroll.view", "payroll.run", "payroll.approve"]}>
              <AppLayout>
                <PayrollRunPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Bank file builder — HR shortlists any mix of employees /
            interns / contractors and downloads an ICICI bulk-upload
            file. Kept separate from the payroll register (HR
            direction 2026-07-28). Gated on payroll.admin because
            these files disburse cash. */}
        <Route
          path="/payroll/bank-file"
          element={
            <ProtectedRoute permissions={["payroll.admin"]}>
              <AppLayout>
                <BankFileBuilderPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Plural alias — the sidebar already links "/payroll/bank-files"
            (from a stale earlier config). Point it at the new builder
            so the sidebar entry works without an extra edit. */}
        <Route
          path="/payroll/bank-files"
          element={
            <ProtectedRoute permissions={["payroll.admin"]}>
              <AppLayout>
                <BankFileBuilderPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll/payslips/me"
          element={
            <WithLayout>
              <MyPayslipsPage />
            </WithLayout>
          }
        />
        <Route
          path="/payroll/payslips/historical/upload"
          element={
            <ProtectedRoute permissions={["payroll.admin"]}>
              <AppLayout>
                <HistoricalPayslipsUploadPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll/tax-declaration/me"
          element={
            <WithLayout>
              <MyTaxDeclarationPage />
            </WithLayout>
          }
        />
        <Route
          path="/payroll/tax-declarations"
          element={
            <ProtectedRoute permissions={["payroll.view", "payroll.approve", "payroll.admin"]}>
              <AppLayout>
                <TaxDeclarationsAdminPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll/statutory-reports"
          element={
            <ProtectedRoute permissions={["payroll.view", "payroll.run", "payroll.approve", "payroll.admin"]}>
              <AppLayout>
                <StatutoryReportsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll/components"
          element={
            <ProtectedRoute permissions={["payroll.view", "payroll.run", "payroll.approve", "payroll.admin"]}>
              <AppLayout>
                <SalaryComponentsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll/loans"
          element={
            <ProtectedRoute permissions={["payroll.view", "payroll.run", "payroll.approve", "payroll.admin"]}>
              <AppLayout>
                <LoansPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll/loans/report"
          element={
            <ProtectedRoute permissions={["payroll.view", "payroll.run", "payroll.approve", "payroll.admin"]}>
              <AppLayout>
                <LoansReportPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll/variable-pay"
          element={
            <ProtectedRoute permissions={["payroll.run", "payroll.approve", "payroll.admin"]}>
              <AppLayout>
                <VariablePayPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll/fnf-certificates"
          element={
            <ProtectedRoute permissions={["payroll.view", "payroll.run", "payroll.approve", "payroll.admin"]}>
              <AppLayout>
                <FnFCertificatesPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll/perquisites"
          element={
            <ProtectedRoute permissions={["payroll.view", "payroll.run", "payroll.approve", "payroll.admin"]}>
              <AppLayout>
                <PerquisitesPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll/salary-revisions"
          element={
            <ProtectedRoute permissions={["payroll.run", "payroll.approve", "payroll.admin"]}>
              <AppLayout>
                <SalaryRevisionsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll/salary-structure/:employeeId"
          element={
            <ProtectedRoute permissions={["payroll.view", "payroll.run", "payroll.approve"]}>
              <AppLayout>
                <SalaryStructurePage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Performance — HR view permission-gated (any of performance.view / performance.manage) */}
        <Route
          path="/performance"
          element={
            <ProtectedRoute permissions={["performance.view", "performance.manage"]}>
              <AppLayout>
                <PerformanceDashboardPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/performance/goals"
          element={
            <WithLayout>
              <GoalsPage />
            </WithLayout>
          }
        />
        <Route
          path="/performance/pip"
          element={
            <ProtectedRoute permissions={["performance.pip"]}>
              <AppLayout>
                <PIPTrackerPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/performance/pip/my"
          element={
            <WithLayout>
              <MyPIPPage />
            </WithLayout>
          }
        />

        {/* Disciplinary & Grievance — cases require auth, whistleblower is public */}
        <Route
          path="/disciplinary"
          element={
            <ProtectedRoute permissions={["disciplinary.view", "disciplinary.manage"]}>
              <AppLayout>
                <DisciplinaryCasesPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Whistleblower portal — NOT wrapped in ProtectedRoute (public) */}
        <Route path="/whistleblower" element={<WhistleblowerPortalPage />} />
        {/* Ethics Committee admin queue — gated on whistleblower.investigate */}
        <Route
          path="/whistleblower/admin"
          element={
            <ProtectedRoute permissions={["whistleblower.investigate"]}>
              <AppLayout>
                <WhistleblowerAdminPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Redundancy Management */}
        <Route
          path="/redundancy"
          element={
            <ProtectedRoute permissions={["separation.initiate", "separation.approve"]}>
              <AppLayout>
                <RedundancyPlanPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Expenses — My Claims page is the universal landing for every employee.
            #222: previously gated by expense.list / expense.admin which plain
            employees don't hold, locking them out of their own claims. The page
            calls `GET /expenses/my` server-side, and ownership is enforced there;
            no need for a route-level permission. Admin "All Claims" lives at
            /expenses/admin below (#223). */}
        <Route
          path="/expenses"
          element={
            <ProtectedRoute>
              <AppLayout>
                <ExpenseListPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/expenses/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <ExpenseDetailPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* #223 — admin "All Claims" view. Sidebar links here; previously
            unrouted (404) for hr_admin / finance. Same component as /expenses
            but `mode="admin"` swaps the data source to /expenses/ (all-claims).
            Backend gates that endpoint on expense.list / expense.admin. */}
        <Route
          path="/expenses/admin"
          element={
            <ProtectedRoute permissions={["expense.list", "expense.admin"]}>
              <AppLayout>
                <ExpenseListPage mode="admin" />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Expense Categories — HR admin surface (categories CRUD +
            per-category approver chain). RBAC re-enforced in the
            page; this gate just keeps employees from landing here. */}
        <Route
          path="/expenses/admin/categories"
          element={
            <ProtectedRoute permissions={["expense.admin"]}>
              <AppLayout>
                <ExpenseCategoriesAdminPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Approver dashboard — every authenticated employee. Backend
            scopes results to claims where the caller is the current
            level's approver. */}
        <Route
          path="/expenses/pending-my-approval"
          element={
            <ProtectedRoute>
              <AppLayout>
                <ExpensePendingApprovalsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Engagement */}
        <Route
          path="/engagement/recognition"
          element={
            <WithLayout>
              <RecognitionFeedPage />
            </WithLayout>
          }
        />

        {/* Separation & FnF — /resign, /initiate and /approvals must come before /:id */}
        <Route
          path="/separation/resign"
          element={
            <WithLayout>
              <SelfResignationPage />
            </WithLayout>
          }
        />
        <Route
          path="/separation/initiate"
          element={
            <ProtectedRoute permissions={["separation.initiate"]}>
              <AppLayout>
                <InitiateExitPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/separation/approvals"
          element={
            <ProtectedRoute permissions={["separation.approve", "leave.approve"]}>
              <AppLayout>
                <ResignationApprovalsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/separation"
          element={
            <ProtectedRoute permissions={["separation.initiate", "separation.approve"]}>
              <AppLayout>
                <SeparationDashboardPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/separation/notice-period"
          element={
            <ProtectedRoute permissions={["separation.initiate", "separation.approve"]}>
              <AppLayout>
                <NoticePeriodPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Visa & Immigration (#123) */}
        <Route
          path="/visa"
          element={
            <ProtectedRoute permission="visa.manage">
              <AppLayout>
                <VisaDashboardPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/separation/:id"
          element={
            <WithLayout>
              <SeparationDetailPage />
            </WithLayout>
          }
        />

        {/* Compensation Revisions (#122) */}
        <Route
          path="/compensation"
          element={
            <ProtectedRoute permission="compensation.view_all">
              <AppLayout>
                <CompensationRevisionsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Learning & Development */}
        <Route
          path="/learning"
          element={
            <WithLayout>
              <LearningDashboardPage />
            </WithLayout>
          }
        />
        <Route
          path="/learning/compliance"
          element={
            <WithLayout>
              <SkillDevelopmentCompliancePage />
            </WithLayout>
          }
        />
        <Route
          path="/learning/courses/:id"
          element={
            <WithLayout>
              <CoursePlayerPage />
            </WithLayout>
          }
        />

        {/* Compliance Training (#124) */}
        <Route
          path="/compliance-training"
          element={
            <ProtectedRoute permission="compliance_training.manage">
              <AppLayout>
                <ComplianceTrainingPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Accommodation & Disability */}
        <Route
          path="/accommodation"
          element={
            <WithLayout>
              <AccommodationRequestPage />
            </WithLayout>
          }
        />

        {/* Manager Self-Service Portal */}
        <Route
          path="/manager"
          element={
            <ProtectedRoute
              permissions={["leave.approve"]}
              roles={["manager", "hr_admin", "hr_country", "superadmin"]}
            >
              <AppLayout>
                <ManagerDashboardPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/approvals"
          element={
            <ProtectedRoute
              permissions={["leave.approve"]}
              roles={["manager", "hr_admin", "hr_country", "superadmin"]}
            >
              <AppLayout>
                <ApprovalHubPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/analytics"
          element={
            <ProtectedRoute
              permissions={["leave.approve"]}
              roles={["manager", "hr_admin", "hr_country", "superadmin"]}
            >
              <AppLayout>
                <TeamAnalyticsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* /manager/delegations route removed 2026-05-28 — Delegation page
            deprecated from the manager UI. Backend route lives on for
            re-introduction if product wants it back. */}

        {/* Analytics */}
        <Route
          path="/analytics"
          element={
            <ProtectedRoute permissions={["reports.view", "reports.admin"]}>
              <AppLayout>
                <AnalyticsDashboardPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics/reports"
          element={
            <ProtectedRoute permissions={["reports.admin"]}>
              <AppLayout>
                <ReportBuilderPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics/headcount"
          element={
            <ProtectedRoute permissions={["reports.view", "reports.admin"]}>
              <AppLayout>
                <HeadcountDetailPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics/attrition"
          element={
            <ProtectedRoute permissions={["reports.view", "reports.admin"]}>
              <AppLayout>
                <AttritionDetailPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics/leave"
          element={
            <ProtectedRoute permissions={["reports.view", "reports.admin"]}>
              <AppLayout>
                <LeaveDetailPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics/performance"
          element={
            <ProtectedRoute permissions={["reports.view", "reports.admin"]}>
              <AppLayout>
                <PerformanceDetailPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics/compliance"
          element={
            <ProtectedRoute permissions={["reports.view", "reports.admin"]}>
              <AppLayout>
                <ComplianceDetailPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Admin — user management is superadmin-only.
            #221: settings.manage was the prior gate but hr_admin / it_admin
            hold that perm too; the page mounted but every API call 403'd,
            leaving a broken loading state. */}
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute superadminOnly>
              <AppLayout>
                <ManageUsersPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* HR-admin-visible admin routes: hr_admin / hr_country / superadmin.
            settings.manage is held by hr_admin so the API allows them through;
            opening the route gate keeps the UI consistent. */}
        <Route
          path="/admin/hr-notifications"
          element={
            <ProtectedRoute permissions={["settings.manage"]}>
              <AppLayout>
                <HRNotificationSettingsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/holidays"
          element={
            <ProtectedRoute permissions={["settings.manage"]}>
              <AppLayout>
                <HolidayAdminPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/org-structure"
          element={
            <ProtectedRoute permissions={["settings.manage"]}>
              <AppLayout>
                <OrgStructurePage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/legal-entities"
          element={
            <ProtectedRoute permissions={["settings.manage"]}>
              <AppLayout>
                <LegalEntitiesAdminPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/legal-entities/reports"
          element={
            <ProtectedRoute permissions={["settings.manage"]}>
              <AppLayout>
                <LegalEntityReportsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/slack-channels"
          element={
            <ProtectedRoute permissions={["settings.manage"]}>
              <AppLayout>
                <SlackChannelsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Documents inbox — HR-admin review queue. Gated on `employee.edit`
            which is held by hr_admin / hr_country / superadmin (the same
            roles the API enforces). */}
        <Route
          path="/admin/documents"
          element={
            <ProtectedRoute permissions={["employee.edit"]}>
              <AppLayout>
                <AdminDocumentsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/deprovision-audit"
          element={
            <ProtectedRoute permissions={["system.deprovision_audit.read"]}>
              <AppLayout>
                <DeprovisionAuditPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/audit-log"
          element={
            <ProtectedRoute permissions={["system.audit.read"]}>
              <AppLayout>
                <SystemAuditLogPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/perks"
          element={
            <ProtectedRoute permissions={["employee.edit"]}>
              <AppLayout>
                <PerksAdminPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Onboarding Feedback — admin view, HR-shaped. Must be declared
            before /:assignmentId. hr_admin / hr_country own onboarding. */}
        <Route
          path="/engagement/onboarding-feedback/admin"
          element={
            <ProtectedRoute permissions={["employee.edit"]}>
              <AppLayout>
                <OnboardingFeedbackAdminPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/engagement/onboarding-feedback/templates"
          element={
            <ProtectedRoute permissions={["employee.edit"]}>
              <AppLayout>
                <OnboardingFeedbackTemplatesPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Onboarding Feedback — form (any authenticated employee) */}
        <Route
          path="/engagement/onboarding-feedback/:assignmentId"
          element={
            <ProtectedRoute>
              <AppLayout>
                <OnboardingFeedbackFormPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Engagement Surveys — HR builder + list. RBAC is enforced
            again inside the page; this gate just keeps employees
            from landing on the URL. */}
        <Route
          path="/engagement/surveys/admin"
          element={
            <ProtectedRoute permissions={["employee.edit"]}>
              <AppLayout>
                <EngagementSurveysAdminPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* My Surveys — every authenticated employee */}
        <Route
          path="/my/surveys"
          element={
            <ProtectedRoute>
              <AppLayout>
                <MySurveysPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/my/surveys/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <MySurveySubmitPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Pre-Onboarding portal — public, no auth (token-based) */}
        <Route path="/pre-onboarding/:token" element={<PreOnboardingPortal />} />

        {/* Kiosk anonymous-survey link — no auth, no layout. The
            backend endpoints (/engagement-surveys/anonymous/*) are
            whitelisted in AuthMiddleware.PUBLIC_PREFIXES. CF Access
            policy at the edge restricts who can reach this URL in
            production. */}
        <Route
          path="/anonymous-survey/:token"
          element={<PublicAnonymousSurveyPage />}
        />

        {/* IT clearance form portal — public, token-based */}
        <Route
          path="/it-clearance-form/:token"
          element={<ITClearanceFormPortal />}
        />
        {/* IT clearance form preview — HR/IT only, gated like the inbox */}
        <Route
          path="/assets/clearance/form-preview"
          element={
            <ProtectedRoute permissions={["assets.view", "assets.manage"]}>
              <AppLayout>
                <ITClearanceFormPreviewPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Pre-Onboarding admin — HR/admin only */}
        <Route
          path="/recruitment/pre-onboarding"
          element={
            <ProtectedRoute permissions={["recruitment.manage"]}>
              <AppLayout>
                <PreOnboardingAdminPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}
